/* eslint-disable camelcase */
import React, { useCallback, useEffect, useState } from 'react';
import { DataGrid, DataGridModel } from 'src/shared/components/organisms/dataGrid';
import { GridColDef, GridSortModel, GridCellParams, GridRowSelectionModel } from '@mui/x-data-grid';
import { Check, Clear, DoNotDisturb } from '@mui/icons-material';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useTranslation } from 'react-i18next';
import { extendObjectWithId } from 'src/shared/functions/extendObjectWithId';
import { Button, Box, Divider, IconButton, Typography } from '@mui/material';
import { FixedFooter } from 'src/shared/components/navigations/fixedFooter';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { getMemberUpdate } from 'src/shared/services/memberUpdateService';
import { useScrollIntoView } from 'src/shared/hooks/useScrollIntoView';
import { DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from 'src/shared/models/enums/gridFilterConfig';
import { MemberUpdateRespons } from '../models/memberUpdateRespons';
import { MemberUpdateStatuses } from './memberUpdateStatuses';

type MemberUpdateDataGridProps = {
  pageSize?: number;
  loadingCallback: (loading: boolean) => void;
  confirmClicked: (memberIds: number[], abortMode: boolean) => void;
  reviewClicked: (memberUpdateId: number) => void;
  checkboxSelection: boolean;
};

export const MemberUpdateDataGrid = (props: MemberUpdateDataGridProps) => {
  const { pageSize = DEFAULT_PAGE_SIZE, loadingCallback, confirmClicked, reviewClicked, checkboxSelection } = props;
  const { t } = useTranslation(['common', 'member']);
  const isMounted = useIsMounted();
  const { scrollIntoView, scrollRef } = useScrollIntoView();

  const [selectionModel, setSelectionModel] = React.useState<GridRowSelectionModel>([]);
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'lastName', sort: 'asc' }]);

  const [rows, setRows] = useState<MemberUpdateRespons[]>([]);
  const [data, setData] = React.useState<DataGridModel>({
    loading: true,
    totalRows: 0,
    rowsPerPageOptions: PAGE_SIZE_OPTIONS,
    pageSize: pageSize || DEFAULT_PAGE_SIZE,
    page: 0,
  });

  const updateData = (k, v) => setData((prev) => ({ ...prev, [k]: v }));

  const onPageChange = (newPageIndex: number) => {
    updateData('page', newPageIndex);
    scrollIntoView();
  };

  const onPageSizeChange = (newPageSize: number) => {
    updateData('page', 0);
    updateData('pageSize', newPageSize);
    scrollIntoView();
  };

  const fetchData = useCallback(async () => {
    updateData('loading', true);
    loadingCallback(true);

    try {
      const result = await getMemberUpdate();

      const memberResult = extendObjectWithId(result.data.results, 'memberUpdate_id');

      if (isMounted()) {
        updateData('totalRows', result.data.totalItems);
        setTimeout(() => {
          setRows(memberResult);
        }, 100);
      }
    } catch (error) {
      if (isMounted()) {
        updateData('loading', false);
        ConsoleHelper.log(error ?? 'Unkown error: getNewsListPageDetails');
      }
    } finally {
      if (isMounted()) {
        updateData('loading', false);
        loadingCallback(false);
      }
    }
  }, [loadingCallback, isMounted]);

  useEffect(() => {
    fetchData();
  }, [data.page, data.pageSize, fetchData]);

  const onRowClick = (params) => {
    reviewClicked(params.row.memberUpdate_id);
  };

  const columns: GridColDef[] = [
    { field: 'memberNr', headerName: t('member:tableHeaders.member_id'), flex: 0.5 },
    {
      field: 'firstName',
      headerName: t('member:tableHeaders.firstName'),
      sortable: true,
      flex: 0.5,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.firstName || ''}</>;
      },
    },
    {
      field: 'lastName',
      headerName: t('member:tableHeaders.lastName'),
      sortable: true,
      flex: 1.25,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.lastName || ''}</>;
      },
    },
    {
      field: 'typeId',
      headerName: t('member:tableHeaders.typeOfUpdate'),
      sortable: false,
      flex: 0.75,
      renderCell: (params: GridCellParams) => {
        const { typeId } = params.row;
        return <MemberUpdateStatuses status={typeId} chipSize='small' />;
      },
    },
    {
      field: 'changedDate',
      headerName: t('member:tableHeaders.changedDate'),
      sortable: false,
      flex: 0.8,
      renderCell: (params: GridCellParams) => {
        const { changedDate } = params.row;
        return changedDate;
      },
    },
    {
      field: 'changedBy',
      headerName: t('member:tableHeaders.changedBy'),
      sortable: false,
      flex: 0.8,
      renderCell: (params: GridCellParams) => {
        const { changedBy } = params.row;
        return changedBy;
      },
    },
  ];

  return (
    <Box
      sx={{
        '& .MuiDataGrid-cell': {
          cursor: 'pointer',
        },
      }}
      ref={scrollRef}
    >
      <DataGrid<MemberUpdateRespons>
        autoHeight
        columns={columns}
        rows={rows}
        dataModel={data}
        disableSelectionOnClick
        checkboxSelection={checkboxSelection}
        onSelectionModelChange={(newSelectionModel) => {
          setSelectionModel(newSelectionModel);
        }}
        sortModel={sortModel}
        onSortModelChange={(newSortModel) => {
          if (newSortModel.length) {
            setSortModel(newSortModel);
          } else {
            setSortModel([{ field: sortModel[0].field, sort: sortModel[0].sort === 'asc' ? 'desc' : 'asc' }]);
          }
        }}
        onRowClick={onRowClick}
        selectionModel={selectionModel}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      />

      {checkboxSelection && selectionModel && selectionModel.length > 0 && (
        <FixedFooter>
          <Box sx={{ p: 1.5, display: 'flex' }}>
            <IconButton onClick={() => setSelectionModel([])} sx={{ pr: 1 }} size='large'>
              <Clear fontSize='small' />
            </IconButton>
            <Typography variant='h6' sx={{ fontWeight: 400 }}>
              {t('common:statusMessages.footerSelectedCount', { count: selectionModel.length })}
            </Typography>
            <Divider orientation='vertical' flexItem sx={{ mx: 1.5 }} />
            <Button
              variant='contained'
              disableElevation
              endIcon={<Check fontSize='small' />}
              onClick={() => {
                const selectedIDs = new Set(selectionModel);
                const membersFromSelection = rows.filter((row) => selectedIDs.has(row.memberUpdate_id || 0)).map((x) => x.memberUpdate_id);
                if (membersFromSelection) confirmClicked(membersFromSelection, false);
              }}
              sx={{ mr: 1 }}
            >
              {t('common:button.accept')}
            </Button>
            <Button
              variant='outlined'
              disableElevation
              endIcon={<DoNotDisturb fontSize='small' />}
              onClick={() => {
                const selectedIDs = new Set(selectionModel);
                const membersFromSelection = rows.filter((row) => selectedIDs.has(row.memberUpdate_id || 0)).map((x) => x.memberUpdate_id);
                if (membersFromSelection) confirmClicked(membersFromSelection, true);
              }}
            >
              {t('common:button.abort')}
            </Button>
          </Box>
        </FixedFooter>
      )}
    </Box>
  );
};
